import { useEffect, useRef } from 'react';

/**
 * **치는 대로 찾는다.** 입력이 멎으면(기본 300ms) 검색어를 적용한다.
 *
 * 왜 (2026-08-01 사용자 지적): 서버 검색은 이미 전 목록이 부분일치(`ilike '%q%'`)인데,
 * 화면이 **`검색`·`찾기` 버튼을 눌러야만** 질의를 보냈다. 편성 찾기 칸에 `김` 을 치고 기다리면
 * 아무것도 안 나오니 "부분검색이 안 된다"로 읽힌다 — 실제로는 서버가 774건을 갖고 있었다.
 * 즉 문제는 매칭 규칙이 아니라 **언제 찾는가**였다.
 *
 * ⚠️ **버튼과 Enter 는 그대로 둔다.** 한 글자만 치고 바로 보고 싶을 때가 있고,
 *    무엇보다 "눌러서 찾는다"를 몸에 익힌 사람이 그 버튼이 사라지면 멈춘다.
 * ⚠️ 첫 렌더는 건너뛴다 — 마운트하자마자 적용하면 기억해 둔 페이지가 1페이지로 되돌아간다.
 * ⚠️ 콜백은 ref 로 잡는다. 호출부가 인라인 함수를 주므로 의존성에 넣으면 **매 렌더마다
 *    타이머가 다시 걸려** 입력이 멎어도 영영 안 쏜다.
 */
export function useAutoSearch(term: string, apply: (q: string) => void, ms = 300) {
  const applyRef = useRef(apply);
  applyRef.current = apply;
  const first = useRef(true);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    const id = setTimeout(() => applyRef.current(term.trim()), ms);
    return () => clearTimeout(id);
  }, [term, ms]);
}

/**
 * 🔎 여러 낱말 검색(#419 · 구 버스인 그리드 찾기) — 화면이 받아 둔 줄을 거를 때 쓰는 쪽.
 * 검색어를 띄어쓰기로 나눠 **하나라도** 맞으면 걸린다(OR). 서버 `functions/src/shared.ts ILIKE_ANY` 와 같은 규칙.
 * 낱말 하나면 예전 `includes(q)` 와 같다. `lower` = 대소문자 무시(예전이 lowercase 였던 화면만 true).
 */
export function searchWords(q: string, lower = true): string[] {
  const s = lower ? q.toLowerCase() : q;
  return s.trim().split(/\s+/).filter((w) => w !== '');
}

/** 값들 중 하나라도 낱말 중 하나를 품으면 참. 낱말이 없으면(빈 검색) 늘 참. */
export function hitAny(words: string[], values: unknown[], lower = true): boolean {
  if (!words.length) return true;
  return values.some((v) => {
    const s = lower ? String(v ?? '').toLowerCase() : String(v ?? '');
    return words.some((w) => s.includes(w));
  });
}
